import { useCallback, useEffect, useRef, useState } from 'react';

import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { mergeRegister } from '@lexical/utils';
import {
  $createTextNode,
  $getSelection,
  $isRangeSelection,
  COMMAND_PRIORITY_LOW,
  RangeSelection,
  SELECTION_CHANGE_COMMAND,
} from 'lexical';

import {
  getLinkNodeFromSelection,
  normalizeUrlScheme,
} from './EditLinkPlugin.utils';
import { EditLinkPluginModalFormValues } from './EditLinkPluginModal.schema';

interface LinkProps {
  url: string;
  text: string;
  target: string | null;
}

const useEditLinkPluginSelection = () => {
  const [editor] = useLexicalComposerContext();
  const [selectedLinkProps, setSelectedLinkProps] = useState<LinkProps | null>(
    null,
  );
  const [lastSelection, setLastSelection] = useState<RangeSelection | null>(
    null,
  );
  const targetRef = useRef<HTMLElement | null>(null);

  const $updateToolbar = useCallback(() => {
    const selection = $getSelection();

    if ($isRangeSelection(selection)) {
      setLastSelection(selection);

      const linkNode = getLinkNodeFromSelection(selection);
      const node = selection.anchor.getNode();

      if (linkNode) {
        setSelectedLinkProps({
          url: linkNode.getURL(),
          text: linkNode.getTextContent(),
          target: linkNode.getTarget() as HTMLAnchorElement['target'] | null,
        });

        const domSelection = window.getSelection();
        if (domSelection && domSelection.rangeCount > 0) {
          targetRef.current = editor.getElementByKey(node.getKey());
        }
      } else {
        setSelectedLinkProps(null);
        targetRef.current = null;
      }
    }
  }, []);

  const linkUrl = selectedLinkProps?.url;
  const isLink = !!linkUrl?.length;

  const handleLinkSubmit = (values: EditLinkPluginModalFormValues) => {
    if (lastSelection) {
      editor.update(() => {
        if (values.url) {
          const selection = $getSelection();
          let linkNode;

          // First tries to find link node from current selection
          if ($isRangeSelection(selection)) {
            linkNode = getLinkNodeFromSelection(selection);
          }

          // If the above fails, we try to fallback to the last known selection
          // we saved in this hooks' state
          if (!linkNode) {
            linkNode = getLinkNodeFromSelection(lastSelection);
          }

          if (linkNode) {
            linkNode.setURL(normalizeUrlScheme(values.url));
            linkNode.setTarget(values.openInNewWindow ? '_blank' : null);

            const currentText = linkNode.getTextContent();

            if (currentText !== values.text) {
              const previousTextNodes = linkNode.getChildren();
              linkNode.append($createTextNode(values.text));
              previousTextNodes.forEach((n) => n.remove());
            }
          }
        }
      });
    }
  };

  useEffect(() => {
    return mergeRegister(
      editor.registerUpdateListener(({ editorState }) => {
        editorState.read(() => {
          $updateToolbar();
        });
      }),
      editor.registerCommand(
        SELECTION_CHANGE_COMMAND,
        (_payload, _newEditor) => {
          $updateToolbar();
          return false;
        },
        COMMAND_PRIORITY_LOW,
      ),
    );
  }, [editor, $updateToolbar]);

  return {
    isLink,
    linkUrl,
    selectedLinkProps,
    handleLinkSubmit,
    targetRef,
  };
};

export default useEditLinkPluginSelection;
